import { useCallback, useEffect, useRef, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import {
  Check,
  Sparkles,
  User2,
  Cpu,
  FileText,
  Zap,
  Brain,
  SlidersHorizontal,
  AlertCircle,
  Headphones,
  type LucideIcon,
} from 'lucide-react'
import { WIZARD_STEPS, type AgentWizardData } from '@/schemas/agent-wizard'

const TAB_ICONS: Record<number, LucideIcon> = {
  0: Sparkles,
  1: User2,
  2: Cpu,
  3: FileText,
  4: Zap,
  5: Brain,
  6: SlidersHorizontal,
  7: Headphones,
}

interface WizardTabsProps {
  currentStep: number
  stepValidation: Record<number, boolean>
  onStepClick: (step: number) => void
  editMode?: boolean
  orientation?: 'horizontal' | 'vertical'
  /** O passo Atendimento só existe para o agente que atende. */
  mostrarAtendimento?: boolean
}

export function WizardTabs({
  currentStep,
  stepValidation,
  onStepClick,
  editMode = false,
  orientation = 'vertical',
  mostrarAtendimento = false,
}: WizardTabsProps) {
  const { t } = useTranslation('agents')
  const { watch } = useFormContext<AgentWizardData>()

  const icon = watch('icon')
  const name = watch('name')

  // Filter out template step in edit mode
  const visibleSteps = (
    editMode ? WIZARD_STEPS.filter((s) => s.id !== 0) : WIZARD_STEPS
  ).filter((s) => s.id !== 7 || mostrarAtendimento)

  if (orientation === 'horizontal') {
    return <HorizontalTabs
      steps={visibleSteps}
      currentStep={currentStep}
      stepValidation={stepValidation}
      onStepClick={onStepClick}
      t={t}
    />
  }

  return (
    <div className="w-52 xl:w-56 flex-shrink-0 flex flex-col h-full">
      {/* Agent mini avatar */}
      <div className="px-3 pb-5 mb-2 border-b border-gray-800/60">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-indigo-500/20 to-purple-500/20 flex items-center justify-center text-xl border border-indigo-500/10 flex-shrink-0">
            {icon || '🤖'}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-white truncate">
              {name || t('wizard.preview.defaultName')}
            </p>
            <p className="text-[11px] text-gray-500">
              {editMode ? t('edit.subtitle') : t('create.subtitle', { count: 6 })}
            </p>
          </div>
        </div>
      </div>

      {/* Tab list */}
      <nav className="flex-1 space-y-0.5 px-1.5 overflow-y-auto">
        {visibleSteps.map((step) => {
          const isActive = step.id === currentStep
          const isValidated = stepValidation[step.id] === true
          const hasErrors = stepValidation[step.id] === false
          // **O PASSO ATENDIMENTO NÃO É "OPCIONAL", É CONDICIONAL.** Ele é
          // `optional` no schema porque o documento do tenant tem ciclo de vida
          // próprio e não entra na validação do formulário do agente — mas para
          // quem lê a barra, "(opcional)" diz "pode pular", e ele só existe
          // para o agente que atende. Achado na validação visual do PR #601.
          const isOptional =
            'optional' in step && step.optional && step.id !== 7
          const Icon = TAB_ICONS[step.id] || FileText

          return (
            <button
              key={step.id}
              type="button"
              onClick={() => onStepClick(step.id)}
              className={cn(
                'relative w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-200 group',
                isActive
                  ? 'bg-indigo-500/10 text-white'
                  : 'text-gray-400 hover:text-gray-200 hover:bg-white/[0.03]'
              )}
            >
              {/* Active indicator bar */}
              {isActive && (
                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-r-full bg-indigo-500 animate-tab-indicator" />
              )}

              {/* Icon */}
              <Icon
                className={cn(
                  'h-4 w-4 flex-shrink-0 transition-colors',
                  isActive ? 'text-indigo-400' : 'text-gray-500 group-hover:text-gray-400'
                )}
              />

              {/* Label */}
              <span className="flex-1 text-left truncate">
                {t(`wizard.stepsConfig.${step.id}.title`)}
                {isOptional && !isActive && (
                  <span className="text-gray-600 ml-1 text-[10px]">
                    {t('wizard.preview.optional')}
                  </span>
                )}
              </span>

              {/* Status indicator */}
              {isValidated && !isActive && (
                <Check className="h-3.5 w-3.5 text-emerald-400 flex-shrink-0" />
              )}
              {hasErrors && !isActive && (
                <div className="h-2 w-2 rounded-full bg-red-400 flex-shrink-0" />
              )}
            </button>
          )
        })}
      </nav>

      {/* Completion summary */}
      <div className="px-3 pt-4 mt-2 border-t border-gray-800/60">
        <CompletionSummary />
      </div>
    </div>
  )
}

/* ─── Horizontal Tabs (Mobile/Tablet) ─── */

interface HorizontalTabsProps {
  steps: readonly (typeof WIZARD_STEPS)[number][]
  currentStep: number
  stepValidation: Record<number, boolean>
  onStepClick: (step: number) => void
  t: ReturnType<typeof useTranslation>['t']
}

function HorizontalTabs({
  steps,
  currentStep,
  stepValidation,
  onStepClick,
  t,
}: HorizontalTabsProps) {
  // **A TIRA ROLAVA E NINGUÉM SABIA.** Medido no tablet: `scrollWidth` 1072
  // contra `clientWidth` 472 — a rolagem funciona, mas a única pista era a
  // barra de 6px, invisível enquanto ninguém arrasta (e permanentemente
  // invisível onde o sistema usa barra sobreposta). O último passo aparecia
  // cortado no meio da palavra, que se lê como defeito, não como "tem mais".
  // As faixas abaixo dizem de que lado ainda há passo.
  const tira = useRef<HTMLDivElement>(null)
  const [sobra, setSobra] = useState({ antes: false, depois: false })

  const medir = useCallback(() => {
    const el = tira.current
    if (!el) return
    const folga = el.scrollWidth - el.clientWidth - el.scrollLeft
    setSobra({ antes: el.scrollLeft > 4, depois: folga > 4 })
  }, [])

  useEffect(() => {
    const el = tira.current
    if (!el) return
    medir()
    const observador = new ResizeObserver(medir)
    observador.observe(el)
    return () => observador.disconnect()
  }, [medir, steps.length])

  // O passo ativo pode estar fora da vista quando a navegação vem dos botões
  // de avançar/voltar, e não do clique na própria tira.
  useEffect(() => {
    tira.current
      ?.querySelector('[data-ativo="true"]')
      ?.scrollIntoView({ block: 'nearest', inline: 'nearest' })
    medir()
  }, [currentStep, medir])

  return (
    <div className="relative">
      <div
        ref={tira}
        onScroll={medir}
        className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-thin -mx-1 px-1"
      >
      {steps.map((step) => {
        const isActive = step.id === currentStep
        const isValidated = stepValidation[step.id] === true
        const hasErrors = stepValidation[step.id] === false
        const Icon = TAB_ICONS[step.id] || FileText

        return (
          <button
            key={step.id}
            type="button"
            data-ativo={isActive}
            onClick={() => onStepClick(step.id)}
            className={cn(
              'flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-all duration-200',
              isActive
                ? 'bg-indigo-500/15 text-indigo-300 border border-indigo-500/30 shadow-sm shadow-indigo-500/10'
                : 'text-gray-400 hover:text-gray-200 hover:bg-white/5 border border-transparent'
            )}
          >
            <Icon className={cn('h-3.5 w-3.5', isActive ? 'text-indigo-400' : 'text-gray-500')} />
            <span className="hidden sm:inline">
              {t(`wizard.stepsConfig.${step.id}.title`)}
            </span>
            {isValidated && !isActive && (
              <Check className="h-3 w-3 text-emerald-400" />
            )}
            {hasErrors && !isActive && (
              <AlertCircle className="h-3 w-3 text-red-400" />
            )}
          </button>
        )
      })}
      </div>

      {sobra.antes && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-background to-transparent"
        />
      )}
      {sobra.depois && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-background to-transparent"
        />
      )}
    </div>
  )
}

/* ─── Completion Summary ─── */

function CompletionSummary() {
  const { t } = useTranslation('agents')
  const { watch } = useFormContext<AgentWizardData>()
  const formData = watch()

  const items = [
    { done: Boolean(formData.name && formData.name.length >= 2) },
    { done: Boolean(formData.provider && formData.model) },
    { done: Boolean(formData.system_prompt) },
    { done: Boolean(formData.tool_ids?.length) },
    { done: Boolean(formData.knowledge_base_ids?.length) },
  ]

  const doneCount = items.filter((i) => i.done).length
  const total = items.length
  const percent = Math.round((doneCount / total) * 100)

  // SVG ring params
  const size = 36
  const strokeWidth = 3
  const radius = (size - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius
  const offset = circumference - (percent / 100) * circumference

  return (
    <div className="flex items-center gap-3">
      <svg width={size} height={size} className="completion-ring flex-shrink-0">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={strokeWidth}
          className="completion-ring-track"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className="completion-ring-fill"
        />
      </svg>
      <div>
        <p className="text-xs font-medium text-gray-300">
          {doneCount}/{total} {t('wizard.preview.checklist')}
        </p>
        <p className="text-[10px] text-gray-500">{percent}%</p>
      </div>
    </div>
  )
}

// Keep backward-compatible export name
export { WizardTabs as StepIndicator }
